/**
 * Whether a funding-source save is in flight, and the error from the last failed one. Hosts pass
 * both to the account and projection screens so they can disable actions while saving and show the
 * failure with a way to try again (refetching the client discards the failed change).
 */
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { fetchClientByClientIdRequest } from '@client/model';
import {
  selectActiveFundingSourceOperation,
  selectFundingSourceSaveError,
  selectMaybeClient,
} from '@client/model/selectors';

export function useFundingSourcesState(): {
  isSaving: boolean;
  saveError: { message: string; retry: () => void } | null;
} {
  const dispatch = useDispatch();
  const client = useSelector(selectMaybeClient);
  const activeOperation = useSelector(selectActiveFundingSourceOperation);
  const errorMessage = useSelector(selectFundingSourceSaveError);
  const clientId = client?.clientId;

  const retry = useCallback(() => {
    if (clientId) dispatch(fetchClientByClientIdRequest(clientId));
  }, [dispatch, clientId]);

  return {
    isSaving: !!activeOperation,
    saveError: errorMessage ? { message: errorMessage, retry } : null,
  };
}
